import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ChevronRight } from 'lucide-react';
import { foundation } from '../../../ui';
import { bg } from '../../../ui/theme/semanticTokens';
import MfaAuthenticatorAppList from './MfaAuthenticatorAppList';
import CalmBanner from '../../common/notice/CalmBanner';
import AuthFieldMessage from '../AuthFieldMessage';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { mfaStepTitleStyle, mfaStepSubStyle } from './mfaStyles';

const f = foundation;
const fc = f.color;

const titleStyle = mfaStepTitleStyle;
const subStyle = mfaStepSubStyle;
const planStyle = { listStyle: 'none', margin: `0 0 ${f.spacePx[4]}`, padding: 0, display: 'flex', flexDirection: 'column', gap: f.spacePx[3] };
const planItemStyle = { display: 'flex', gap: f.spacePx[3], alignItems: 'flex-start', fontSize: f.fontSize.sm, color: fc.neutral.text, fontFamily: f.fontFamily.sans };
const planBadge = { flexShrink: 0, width: 24, height: 24, borderRadius: '50%', backgroundColor: fc.brand.primaryMuted, color: fc.brand.text, fontWeight: f.fontWeight.bold, fontSize: f.fontSize.xs, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
const noteGap = { marginBottom: f.spacePx[4] };
const discStyle = { border: `1px solid ${fc.neutral.border}`, borderRadius: f.radii.md, overflow: 'hidden', marginBottom: f.spacePx[4] };
const discButtonStyle = { width: '100%', border: 'none', background: bg.surface, display: 'flex', alignItems: 'center', gap: f.spacePx[2], padding: `${f.spacePx[3]} ${f.spacePx[3]}`, minHeight: 44, font: 'inherit', fontFamily: f.fontFamily.sans, fontWeight: f.fontWeight.semibold, fontSize: f.fontSize.sm, color: fc.neutral.text, cursor: 'pointer', textAlign: 'left' };

/**
 * RD-SEC-015 flow A/B/C/D — step 0 ("Antes de empezar"). Same screen for
 * Account and both forced gates; `gate` swaps the title/subtitle to the
 * "your company requires it" copy. `resetNote` (mfaResetNoteCache) renders
 * the amber "delete the old entry" warning (`'reset'`) or a calm blue
 * one-liner (`'selfOff'`, the user's own turn-off) above everything else
 * when the user's last MFA event was a removal (flow D) — no nested AutoHeight; the parent
 * MfaStepper's single AutoHeight already tweens this toggle.
 *
 * RD-SEC-015 (2026-09-25 anatomy fix): the "Empezar" CTA now lives in the
 * caller's PINNED footer (`useMfaStepperUi`'s `footer` slot), not inline
 * here — this step renders content only, so it never needs to know `busy`
 * or `onStart`.
 */
export default function MfaStepIntro({ gate = false, resetNote = null, errorMessage = '' }) {
  const intl = useIntl();
  const reducedMotion = useReducedMotion();
  const [appListOpen, setAppListOpen] = useState(false);

  return (
    <div data-testid="mfa-gate-intro">
      {resetNote === 'selfOff' ? (
        <CalmBanner tone="info" testId="mfa-reset-note-selfoff" style={noteGap}>
          {intl.formatMessage({
            id: 'mfaStepper.reset.selfOff',
            defaultMessage: 'Still have “CrewVex” in your app? Delete it before you continue so the codes don’t get mixed up.',
          })}
        </CalmBanner>
      ) : null}
      {resetNote === 'reset' ? (
        <CalmBanner
          tone="warning"
          testId="mfa-reset-note-reset"
          style={noteGap}
          title={intl.formatMessage({ id: 'mfaStepper.reset.title', defaultMessage: 'Delete the old entry' })}
        >
          {intl.formatMessage({
            id: 'mfaStepper.reset.body',
            defaultMessage: 'Your verification was reset and the old code no longer works. Delete “CrewVex” from your app before you continue.',
          })}
        </CalmBanner>
      ) : null}

      <p style={titleStyle} data-testid="mfa-step-title">
        {gate
          ? intl.formatMessage({ id: 'mfaStepper.intro.titleGate', defaultMessage: 'Turn on two-step verification' })
          : intl.formatMessage({ id: 'mfaStepper.intro.title', defaultMessage: 'Protect your account' })}
      </p>
      <p style={subStyle} data-testid="mfa-step-sub">
        {gate
          ? intl.formatMessage({ id: 'mfaStepper.intro.subGate', defaultMessage: 'Your company requires it. Takes 2 minutes.' })
          : intl.formatMessage({ id: 'mfaStepper.intro.sub', defaultMessage: 'A code from your phone, plus your password. Takes 2 minutes.' })}
      </p>

      <ol style={planStyle} data-cvx-mfa-beat="plan">
        <li style={planItemStyle}><span style={planBadge} aria-hidden>1</span>{intl.formatMessage({ id: 'mfaStepper.intro.p1', defaultMessage: 'Add CrewVex to your app' })}</li>
        <li style={planItemStyle}><span style={planBadge} aria-hidden>2</span>{intl.formatMessage({ id: 'mfaStepper.intro.p2', defaultMessage: 'Enter the code' })}</li>
        <li style={planItemStyle}><span style={planBadge} aria-hidden>3</span>{intl.formatMessage({ id: 'mfaStepper.intro.p3', defaultMessage: 'Save your recovery codes' })}</li>
      </ol>

      <div style={discStyle} data-testid="mfa-no-app">
        <button type="button" style={discButtonStyle} onClick={() => setAppListOpen((v) => !v)} aria-expanded={appListOpen}>
          <span style={{ flex: 1 }}>{intl.formatMessage({ id: 'mfaStepper.noApp', defaultMessage: 'No app yet?' })}</span>
          <ChevronRight size={16} aria-hidden style={{ transform: appListOpen ? 'rotate(90deg)' : 'none', transition: reducedMotion ? 'none' : 'transform 200ms ease', color: fc.neutral.iconMuted }} />
        </button>
        {appListOpen ? (
          <div style={{ padding: `0 ${f.spacePx[3]} ${f.spacePx[3]}` }}>
            <MfaAuthenticatorAppList />
          </div>
        ) : null}
      </div>

      {/* Same attempt-result line every other step uses (eases open/closed,
          one stable live region) — never an ad-hoc alert paragraph. */}
      <AuthFieldMessage
        testId="mfa-intro-error"
        message={errorMessage
          ? intl.formatMessage({ id: 'twoFactor.error.couldNotStart', defaultMessage: 'Could not start 2FA setup.' })
          : ''}
      />
    </div>
  );
}
